import { useCallback, useEffect, useRef, useState } from 'react';
import { createNucliaService, NucliaService, RaoStep, RaoResult, RaoSourceContext, generateConversationId } from '../services/nuclia-service';
import { ChatMessage, ChatSource } from '../types';
import { DEFAULT_USER, BOT_USER } from '../constants';
import { useAuth } from '../contexts/AuthContext';
import {
    mapRaoStepsToReasoningSteps,
    mapSourceContextToSources,
    mapAllSourceContextsToSources,
    extractSummaryFromSteps
} from '../utils';
import { Visualization } from '../lib/rao-types';
import { buildCitationLinks } from '../lib/chat/buildCitationLinks';

export interface UseNucliaChatOptions {
    onMessageUpdate?: (messages: ChatMessage[]) => void;
}

export interface UseNucliaChatReturn {
    messages: ChatMessage[];
    isLoading: boolean;
    askQuestion: (questionText: string, documentId: string, enableInternetSearch: boolean, enableRestrictedInternal: boolean) => void;
    clearMessages: () => void;
    isServiceReady: boolean;
    serviceError: string | null;
}

export function useNucliaChat(options?: UseNucliaChatOptions): UseNucliaChatReturn {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isServiceReady, setIsServiceReady] = useState(false);
    const [serviceError, setServiceError] = useState<string | null>(null);
    const { nucliaSessionId } = useAuth();
    
    const nucliaServiceRef = useRef<NucliaService | null>(null);
    const startTimeRef = useRef<number>(0);
    const botMessageIdRef = useRef<number>(0);

    // Initialize service asynchronously
    useEffect(() => {
        let mounted = true;
        
        createNucliaService()
            .then(service => {
                if (mounted) {
                    nucliaServiceRef.current = service;
                    setIsServiceReady(true);
                }
            })
            .catch(error => {
                if (mounted) {
                    const errorMessage = error instanceof Error ? error.message : 'Failed to initialize service';
                    setServiceError(errorMessage);
                }
            });

        return () => {
            mounted = false;
        };
    }, []);

    const askQuestion = useCallback((
        questionText: string,
        documentId: string,
        enableInternetSearch: boolean,
        enableRestrictedInternal: boolean
    ) => {
        if (!questionText.trim()) return;
        
        if (!nucliaServiceRef.current) {
            console.error('[useNucliaChat] Service not ready yet');
            return;
        }

        setIsLoading(true);
        startTimeRef.current = Date.now();

        const userMessageId = Date.now();
        const userMessage: ChatMessage = {
            id: userMessageId,
            author: DEFAULT_USER,
            text: questionText,
        };

        botMessageIdRef.current = userMessageId + 1;
        const botMessage: ChatMessage = {
            id: botMessageIdRef.current,
            author: BOT_USER,
            text: '',
            isLoading: true,
            reasoning: {
                steps: [],
                isLoading: true
            },
            sources: []
        };

        setMessages(prev => [...prev, userMessage, botMessage]);

        const accumulatedSteps: RaoStep[] = [];
        let accumulatedSources: ChatSource[] = [];

        const sessionId = nucliaSessionId || generateConversationId();

        nucliaServiceRef.current.askQuestion(
            {
                query: questionText,
                documentName: documentId,
                enableInternetSearch: enableInternetSearch,
                enableRestrictedInternal: enableRestrictedInternal,
                conversationId: sessionId,
            },
            {
                onTextUpdate: (text: string) => {
                    console.log('[Nuclia] Text update:', text);
                    setMessages(prev => prev.map(msg => 
                        msg.id === botMessageIdRef.current 
                            ? { ...msg, text }
                            : msg
                    ));
                },
                onReceiveVisualizations: (dataVisualizations: Visualization[]) => {
                    console.log('[Nuclia] DataVisualization received:', dataVisualizations);
                    setMessages(prev => prev.map(msg => 
                        msg.id === botMessageIdRef.current 
                            ? { ...msg, text: `Generated ${dataVisualizations.length} chart(s), they'll be displayed when this response is complete.` }
                            : msg
                    ));
                },
                onStep: (step: RaoStep) => {
                    console.log('[Nuclia] Step:', step);
                    accumulatedSteps.push(step);
                    const reasoningSteps = mapRaoStepsToReasoningSteps(accumulatedSteps);
                    
                    setMessages(prev => prev.map(msg => 
                        msg.id === botMessageIdRef.current 
                            ? { 
                                ...msg, 
                                reasoning: {
                                    ...msg.reasoning,
                                    steps: reasoningSteps,
                                    isLoading: true
                                }
                            }
                            : msg
                    ));
                },
                onSourceContext: (context: RaoSourceContext) => {
                    console.log('[Nuclia] Source context:', context);
                    const newSources = mapSourceContextToSources(context);
                    accumulatedSources = [...accumulatedSources, ...newSources];
                    
                    setMessages(prev => prev.map(msg => 
                        msg.id === botMessageIdRef.current 
                            ? { 
                                ...msg, 
                                sources: accumulatedSources
                            }
                            : msg
                    ));
                },
                onComplete: (result: RaoResult) => {
                    console.log('[Nuclia] Complete:', result);
                    const elapsedSeconds = Math.round((Date.now() - startTimeRef.current) / 1000);
                    const allSources = accumulatedSources.length > 0 
                        ? accumulatedSources 
                        : mapAllSourceContextsToSources(result.sourceContexts);
                    const finalReasoningSteps = mapRaoStepsToReasoningSteps(result.steps);
                    const summary = extractSummaryFromSteps(result.steps);
                    const finalDataVisualizations = result.dataVisualizations && result.dataVisualizations.length > 0
                        ? result.dataVisualizations
                        : undefined;
                    
                    // Build citation links from metadata and source contexts
                    const citationLinks = buildCitationLinks(result.citationMetadata, result.sourceContexts);
                    console.log('[Nuclia] Citation links:', citationLinks);

                    setMessages(prev => prev.map(msg => 
                        msg.id === botMessageIdRef.current 
                            ? { 
                                ...msg, 
                                text: result.answer || 'No response received.',
                                summary,
                                isLoading: false,
                                reasoning: {
                                    timeInSeconds: elapsedSeconds,
                                    steps: finalReasoningSteps,
                                    isLoading: false
                                },
                                sources: allSources.length > 0 ? allSources : undefined,
                                dataVisualizations: finalDataVisualizations,
                                answerUrls: result.answerUrls,
                                tokenCounts: result.tokenCounts,
                                citationLinks: Object.keys(citationLinks).length > 0 ? citationLinks : undefined
                            }
                            : msg
                    ));
                    
                    setIsLoading(false);
                },
                onError: (error: string) => {
                    console.error('[Nuclia] Error:', error);
                    setMessages(prev => prev.map(msg => 
                        msg.id === botMessageIdRef.current 
                            ? { 
                                ...msg, 
                                text: `Error: ${error}`,
                                isLoading: false,
                                reasoning: undefined
                            }
                            : msg
                    ));
                    setIsLoading(false);
                }
            }
        );
    }, [nucliaSessionId]);

    const clearMessages = useCallback(() => {
        setMessages([]);
    }, []);

    return {
        messages,
        isLoading,
        isServiceReady,
        serviceError,
        askQuestion,
        clearMessages
    };
}
